import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAtom } from 'jotai';
import { Fragment } from 'react/jsx-runtime';
import { REPORT_PLATFORM, REPORT_PRIORITY, REPORT_STATUS } from '../lib/constants';
import { cln } from '../lib/utils';
import { Report } from '../types';
import { PATHS } from '../lib/paths';
import { deleteReportConfirmDialogAtom } from '../atoms';
import { Summary } from './Summary';
import { useIsLoggedIn } from '../hooks/useIsLoggedIn';

interface Props {
  report: Report;
}

export function ReportDetails({ report }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setDeleteReportConfirmDialogState] = useAtom(deleteReportConfirmDialogAtom);
  const isSuperAdmin = useIsLoggedIn({ isAdmin: true });

  const handleShareClick = () => {
    navigator.clipboard.writeText(`${import.meta.env.VITE_DOMAIN}${PATHS.reportPath(report.id)}`);
    toast.success(t('common.copiedLink'));
  };

  const summaryComponent = () => (
    <Fragment>
      <i
        className={
          report.platform === REPORT_PLATFORM.MOBILE ? 'ri-smartphone-line' : 'ri-computer-line'
        }
      />
      <span
        className={cln(
          'badge font-medium text-label',
          report.priority === REPORT_PRIORITY.LOW && 'bg-priority-low',
          report.priority === REPORT_PRIORITY.MED && 'bg-priority-medium',
          report.priority === REPORT_PRIORITY.HIGH && 'bg-priority-high',
        )}
      >
        {t(`priority.${report.priority}`)}
      </span>
      <span
        className={cln(
          'badge font-medium text-label',
          report.status === REPORT_STATUS.REPORTED && 'bg-status-reported',
          report.status === REPORT_STATUS.IN_PROGRESS && 'bg-status-inProgress',
          report.status === REPORT_STATUS.DONE && 'bg-status-done',
        )}
      >
        {t(`report.status.${report.status}`)}
      </span>
    </Fragment>
  );

  const subtitle = () => (
    <div className="flex">
      {new Date(report.createdAt).toLocaleDateString()}
      {isSuperAdmin && report.key && (
        <Fragment>
          <div className="mx-2">-</div>
          <div className="font-bold">
            <a href={report.jiraUrl}>{report.key}</a>
          </div>
        </Fragment>
      )}
    </div>
  );

  return (
    <Summary title={report.name} summaryComponent={summaryComponent()} subtitle={subtitle()}>
      <div className="grid grid-cols-[min-content_auto] gap-5 pt-10 pb-3">
        <span className="font-semibold">{t('report.env')}</span>
        <span>{t(`environment.${report.env}`)}</span>

        {report.platform === REPORT_PLATFORM.WEB && (
          <>
            <span className="font-semibold">{t('report.browsers')}</span>
            <span>{report.browsers?.join(', ')}</span>
          </>
        )}
        {report.platform === REPORT_PLATFORM.MOBILE && (
          <>
            <span className="font-semibold">{t('report.device')}</span>
            <span>{report.device}</span>

            <span className="font-semibold">{t('report.version')}</span>
            <span>{report.version}</span>
          </>
        )}
        <span className="font-semibold">{t('report.description')}</span>
        <span>{report.currentDescription}</span>
        <div className="flex justify-end items-start gap-4 col-span-2">
          <button
            className="danger-fill mr-auto"
            onClick={() =>
              setTimeout(() => setDeleteReportConfirmDialogState({ isOpen: true, report }), 1)
            }
          >
            <i className="rei-trash" />
            {t('common.delete')}
          </button>
          <button className="primary" onClick={() => navigate(PATHS.reportPath(report.id))}>
            <i className="ri-file-info-line" />
            {t('common.details')}
          </button>
          <button className="primary whitespace-nowrap" onClick={handleShareClick}>
            <i className="ri-share-forward-line" />
            {t('common.copyLink')}
          </button>
        </div>
      </div>
    </Summary>
  );
}
